import React, { useState, useEffect } from "react";
import styles from "./CustomManufacturingSolutions.module.scss";
import CustomizedFormulationsSVG from "./CustomizedFormulationsSVG";
import InHouseQASVG from "./InHouseQASVG";
import ProcessEngineeringSVG from "./ProcessEngineeringSVG";
import TechnicalSupportSVG from "./TechnicalSupportSVG";
import RealTimeVisibilitySVG from "./RealTimeVisibilitySVG";
import ElchemySVG from "./ElchemySVG";
import Xarrow from "react-xarrows";

const customManufacturingSolutions = [
  {
    id: 1,
    title: "Customized Formulations",
    description:
      "We turn your product vision into reality with made-to-order formulations designed for performance, compliance, and speed to market.",
    icon: CustomizedFormulationsSVG,
  },
  {
    id: 2,
    title: "In House QA/QC",
    description:
      "Every batch goes through strict in-house quality checks so you get consistent and high-performing ingredients every time.",
    icon: InHouseQASVG,
  },
  {
    id: 3,
    title: "Process Engineering",
    description:
      "Our deep process expertise helps you scale faster, avoid delays, and improve cost-efficiency from day one.",
    icon: ProcessEngineeringSVG,
  },
  {
    id: 4,
    title: "Technical support",
    description:
      "From sampling to scale-up, our expert team is ready to troubleshoot, adapt, and keep your development moving forward.",
    icon: TechnicalSupportSVG,
  },
  {
    id: 5,
    title: "Real-Time Visibility",
    description:
      "Track key milestones, receive proactive alerts, and get complete visibility throughout your custom manufacturing journey.",
    icon: RealTimeVisibilitySVG,
  },
];

const CustomManufacturingCard = ({ solution, isMobile }) => {
  return (
    <div
      className={`${styles.customManufacturingCard} ${
        isMobile ? styles.mobileCard : ""
      }`}
      id={`card-${solution.id}`}
    >
      {/* Icon */}
      {solution.icon()}
      <div className={styles.contentContainer}>
        {/* Title */}
        <div className={styles.title}>{solution.title}</div>
        {/* Description */}
        <div className={styles.description}>{solution.description}</div>
      </div>
    </div>
  );
};

function CustomManufacturingSolutions() {
  const [isMobile, setIsMobile] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const isFirstSlide = currentSlide === 0;
  const isLastSlide = currentSlide === customManufacturingSolutions.length - 1;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1200);
    };

    handleResize(); // Check on mount
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const nextSlide = () => {
    if (!isLastSlide) setCurrentSlide((prev) => prev + 1);
  };
  const prevSlide = () => {
    if (!isFirstSlide) setCurrentSlide((prev) => prev - 1);
  };

  // Mobile Layout
  if (isMobile) {
    return (
      <div className={styles.customManufacturingSolutions}>
        {/* Small heading */}
        <h6>ELCHEMY EXPERTISE</h6>
        {/* Big Heading */}
        <h2>Custom Manufacturing Solutions</h2>
        <div className={styles.mobileCarousel}>
          <div className={styles.carouselContainer}>
            <div
              className={styles.carouselSlides}
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {customManufacturingSolutions.map((solution) => (
                <div key={solution.id} className={styles.carouselSlide}>
                  <CustomManufacturingCard
                    solution={solution}
                    isMobile={true}
                  />
                </div>
              ))}
            </div>
          </div>
          {/* Navigation Buttons */}
          <div className={styles.carouselNavigation}>
            <button
              className={`${styles.navButton} ${
                isFirstSlide ? styles.disabled : ""
              }`}
              onClick={prevSlide}
              disabled={isFirstSlide}
              aria-label="Previous solution"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M15 18L9 12L15 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              className={`${styles.navButton} ${
                isLastSlide ? styles.disabled : ""
              }`}
              onClick={nextSlide}
              disabled={isLastSlide}
              aria-label="Next solution"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M9 18L15 12L9 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Desktop Layout
  return (
    <div className={styles.customManufacturingSolutions}>
      {/* Small heading */}
      <h6>ELCHEMY EXPERTISE</h6>
      {/* Big Heading */}
      <h2>Custom Manufacturing Solutions</h2>
      <div className={styles.customManufacturingCardsContainer}>
        {/* CustomManufacturingCards */}
        <div className={styles.customManufacturingCards}>
          {customManufacturingSolutions.map((solution) => (
            <CustomManufacturingCard
              key={solution.id}
              solution={solution}
              isMobile={false}
            />
          ))}
        </div>

        {/* Intermediate anchor points for tree structure - hidden on mobile */}
        <div
          id="branch-point-1"
          className={styles.branchPoint}
          style={{
            position: "absolute",
            right: "35%",
            top: "50%",
            width: "1px",
            height: "1px",
            zIndex: 1,
          }}
        ></div>
        <div
          id="branch-point-2"
          className={styles.branchPoint}
          style={{
            position: "absolute",
            right: "25%",
            top: "50%",
            width: "1px",
            height: "1px",
            zIndex: 1,
          }}
        ></div>

        {/* Elchemy Logo */}
        <div className={styles.elchemyLogoContainer} id="elchemy-logo">
          <ElchemySVG />
        </div>
      </div>

      {/* Main trunk line from Elchemy to furthest branch point */}
      <Xarrow
        start="elchemy-logo"
        end="branch-point-2"
        color="rgba(42, 88, 251, 0.2)"
        strokeWidth={2}
        path="straight"
        startAnchor="left"
        endAnchor="right"
        showHead={false}
        showTail={false}
        zIndex={1}
      />

      {/* Connector arrows with branching structure */}
      {customManufacturingSolutions.map((solution) => {
        // Determine which branch point to connect to
        let branchPoint;
        if (solution.id === 1 || solution.id === 5) {
          // Top and bottom cards connect to furthest branch point
          branchPoint = "branch-point-2";
        } else if (solution.id === 2 || solution.id === 4) {
          // Second cards connect to middle branch point
          branchPoint = "branch-point-1";
        } else {
          // Middle card connects directly to Elchemy logo
          branchPoint = "elchemy-logo";
        }

        return (
          <Xarrow
            key={`arrow-${solution.id}`}
            start={`card-${solution.id}`}
            end={branchPoint}
            color="rgba(42, 88, 251, 0.2)"
            strokeWidth={2}
            path="smooth"
            curveness={0.3}
            startAnchor={{
              position: "right",
              offset: { x: 0, y: 0 },
            }}
            endAnchor={{
              position: "left",
              offset: { x: 0, y: 0 },
            }}
            showHead={false}
            showTail={false}
            gridBreak="20%"
            _cpx1Offset={-50}
            _cpx2Offset={50}
            _cpy1Offset={solution.id <= 2 ? -20 : solution.id >= 4 ? 20 : 0}
            _cpy2Offset={solution.id <= 2 ? -20 : solution.id >= 4 ? 20 : 0}
            zIndex={1}
          />
        );
      })}
    </div>
  );
}

export default CustomManufacturingSolutions;
